import {create} from 'zustand';
import type {OttoSessionId} from '@/matic/features/agents/otto/types/otto-models';

/**
 * Cross-subtree bridge for the one genuinely-local transient the consolidated
 * Library rail needs to share with the canvas.
 *
 * The rail (`LibraryRail`, rendered inside `SidebarNav`) and the canvas
 * (`LibraryContainer`) are sibling React subtrees under `ProtectedLayout`, so
 * they can't share component state. Almost everything the rail's pickers need
 * is a projection of the URL + the react-query cache (both global) — but the
 * per-conversation "still starting" spinner has no URL or cache home: it's a
 * bridge over the ~3s gap before the authoritative `activeIds` registry poll
 * first sees a freshly-minted run.
 *
 * The container's mint/streaming handlers add/remove ids from
 * `optimisticRunningIds`; the rail's conversations picker reads it to spin the
 * matching row(s). It's a bridge, not a latch — each id is cleared the
 * instant its own stream reports done/abort/unmount (see
 * `handleChatStreamingChange`), after which the poll alone drives that row's
 * spinner. Multiple concurrent Otto sessions can each be minting at once (see
 * memory: Otto session selection is explicit, multiple sessions stay active),
 * so this is keyed per session id rather than a single scalar — a second
 * mint must not clobber a first one still in its optimistic window.
 *
 * Everything else optimistic (the new conversation row itself) already lives in
 * the `ottoSessionKeys.list` cache, so the rail sees it via `useOttoSessions`
 * with no prop.
 *
 * The same subtree gap breaks the picker↔activity-feed reverse-hover
 * coordination (hovering a Browse co-object lights up matching activity rows,
 * and vice-versa) once the picker lives in the rail and the feed in the canvas.
 * `hoveredCoObjectKey` (picker → feed) and `hoveredActivityRefs` (feed →
 * picker) bridge that here — pure transient UI state, same category as the
 * spinner.
 */
type ChatDraftStore = {
  optimisticRunningIds: ReadonlySet<OttoSessionId>;
  addOptimisticRunningId: (id: OttoSessionId) => void;
  removeOptimisticRunningId: (id: OttoSessionId) => void;
  /** Browse co-object under the cursor (`typeId:recordId`) → the activity feed
   *  highlights matching rows. */
  hoveredCoObjectKey: string | undefined;
  setHoveredCoObjectKey: (key: string | undefined) => void;
  /** Association keys of the activity row under the cursor → Browse highlights
   *  matching "Also tagged" co-objects. */
  hoveredActivityRefs: string[] | undefined;
  setHoveredActivityRefs: (refs: string[] | undefined) => void;
};

export const useChatDraftStore = create<ChatDraftStore>((set) => ({
  optimisticRunningIds: new Set(),
  addOptimisticRunningId(id) {
    set((state) => ({
      optimisticRunningIds: new Set(state.optimisticRunningIds).add(id),
    }));
  },
  removeOptimisticRunningId(id) {
    set((state) => {
      if (!state.optimisticRunningIds.has(id)) return state;
      const next = new Set(state.optimisticRunningIds);
      next.delete(id);
      return {optimisticRunningIds: next};
    });
  },
  hoveredCoObjectKey: undefined,
  setHoveredCoObjectKey(key) {
    set({hoveredCoObjectKey: key});
  },
  hoveredActivityRefs: undefined,
  setHoveredActivityRefs(refs) {
    set({hoveredActivityRefs: refs});
  },
}));
